import { ExternalUrls } from '../lib/constants';
import { useSEO, useBreadcrumbSchema, PageSEO } from '../lib/seo';
import { Box, Typography, Grid, Stack, Container, Button, useTheme } from '@mui/material';
import { Link, createFileRoute } from '@tanstack/react-router';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import AlarmIcon from '@mui/icons-material/Alarm';
import InsightsIcon from '@mui/icons-material/Insights';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import Diversity3Icon from '@mui/icons-material/Diversity3';

import { Section } from '../components/ui/Section';
import { SectionHeader } from '../components/ui/SectionHeader';
import { Hero } from '../components/ui/Hero';
import { BrandBackdrop } from '../components/brand/BrandBackdrop';
import { LogoMarqueeSection } from '../components/sections/LogoMarqueeSection';
import { CallToActionCard } from '../components/ui/CallToActionCard';
import { FeatureIcon } from '../components/ui/FeatureIcon';
import { InfoCard } from '../components/ui/InfoCard';
import { PersonCard, SocialLink } from '../components/ui/PersonCard';
import { ScreenshotShowcase } from '../components/ui/ScreenshotShowcase';
import { StoryCard } from '../components/ui/StoryCard';

interface Member {
  name: string;
  role: string;
  organisation?: string;
  description: string;
  image: string;
  links?: SocialLink[];
}

export const Route = createFileRoute('/about')({
  component: AboutPage,
});

const team: Member[] = [
  {
    name: 'Ellis Parry',
    role: 'CEO & Founder',
    description:
      '2025 NHS Clinical Entrepreneur and Innovate UK awardee. Holds an MEng and DPhil in Engineering Science from Oxford. Ellis combines deep-tech expertise with a decade of lived experience as a caregiver to translate evidence-based neurorehab into accessible daily technology.',
    image: '/images/team/ellis-parry.png',
    links: [{ type: 'linkedin', url: 'https://www.linkedin.com/in/ellis-parry/' }],
  },
  {
    name: 'Tim Coulson',
    role: 'CTO & Founder',
    description: 'Product engineering leader, formerly CTO at Kitt and Backend Lead at JustPark (exit) and Flag Right (Y-Combinator 2022). Tim brings extensive experience in disruptive technology, scaling secure, user-centric technology to established platforms.',
    image: '/images/team/tim-coulson.jpg',
    links: [{ type: 'linkedin', url: 'https://www.linkedin.com/in/timrcoulson/' }],
  },
  {
    name: 'Luke Parry',
    role: 'Patient Voice & Founder',
    description:
      'Brain injury survivor and former Oxford Engineering scholar. Anchors neumind’s design to real-world recovery needs. From being told he might not walk to joining the Paralympic Development Squad, Luke ensures our tools are built for—and validated by—the community we serve.',
    image: '/images/team/luke-parry.png',
    links: [{ type: 'instagram', url: 'https://www.instagram.com/luke_paza' }],
  },
  {
    name: 'Matthew Jamieson',
    role: 'Research & Product',
    description: 'PhD in assistive technology for memory support. As a Research Associate at the University of Glasgow, Matt specialises in longitudinal study design and clinical research methods, ensuring neumind’s product interventions are grounded in rigorous scientific evidence.',
    image: '/images/team/matthew-jamieson.png',
    links: [{ type: 'linkedin', url: 'https://www.linkedin.com/in/matthew-jamieson-2906226a/' }],
  },
  {
    name: 'Rufus Russell',
    role: 'Technical Advisor',
    description: 'Expert in cloud architecture and release discipline. Previously at IBM and Consensys. As an original co-founder, Rufus advises the engineering team on maintaining robust, scalable infrastructure and enterprise-grade testing protocols.',
    image: '/images/team/rufus-russell.png',
    links: [{ type: 'linkedin', url: 'https://www.linkedin.com/in/rufus-russell/' }],
  },
  {
    name: 'Claudia Hill',
    role: 'Strategic Advisor',
    description: 'Biomedical engineer (Oxford DPhil) and Harvard MBA. As a former co-founder, Claudia continues to support neumind’s US strategy and commercialisation, leveraging her current experience in global market strategy at Eli Lilly.',
    image: '/images/team/claudia-hill.png',
    links: [{ type: 'linkedin', url: 'https://www.linkedin.com/in/claudia-hill-phd-15005b7b/' }],
  },
];

const advisors: Member[] = [
  {
    name: 'Prof Jonathan Evans',
    role: 'Professor of Neuropsychology',
    organisation: 'University of Glasgow',
    description: 'Programme Director for the MSc in Clinical Neuropsychology, former International Neuropsychological Society President. A leading authority in mental health and wellbeing, Professor Evans guides the theoretical underpinnings and clinical efficacy of neumind’s cognitive interventions.',
    image: '/images/team/jonathan-evans.png',
    links: [{ type: 'research', url: 'https://orcid.org/0000-0002-9625-2071' }],
  },
  {
    name: 'Dr Jessica Fish',
    role: 'Senior Lecturer in Clinical Neuropsychology',
    organisation: 'University of Glasgow',
    description: "Chief Editor of 'The Neuropsychologist'. With a PhD from the MRC CBU (Cambridge) and clinical tenure at the Oliver Zangwill Centre, Dr. Fish brings deep expertise in prospective memory rehabilitation and acquired brain injury outcomes.",
    image: '/images/team/jessica-fish.png',
    links: [{ type: 'research', url: 'https://orcid.org/0000-0002-9337-1489' }],
  },
  {
    name: 'Prof Andrew Bateman',
    role: 'Professor of Rehabilitation',
    description: 'Professor at the University of Essex, President of ACPIN, and Chair of UKABIF. Having led the Oliver Zangwill Centre for 17 years, he brings decades of leadership in rehabilitation service innovation, assistive technology, and patient-reported outcome measures (PROMs).',
    image: '/images/team/andrew-bateman.png',
    links: [{ type: 'research', url: 'https://orcid.org/0000-0002-2547-5921' }],
  },

];

const features = [
  {
    icon: <AlarmIcon />,
    title: 'Reminders & Routines',
    description: 'Timely prompts for medications, appointments, and daily routines that keep therapy on track.',
  },
  {
    icon: <InsightsIcon />,
    title: 'Trackers & Journals',
    description: 'Log mood, fatigue, and progress with simple sliders. Spot patterns and celebrate wins.',
  },
  {
    icon: <TipsAndUpdatesIcon />,
    title: 'Nudges & Strategies',
    description: 'Gentle prompts and evidence-based techniques delivered when motivation is low.',
  },
  {
    icon: <Diversity3Icon />,
    title: 'Care Circle',
    description: 'Connect clinicians, clients, and carers on one shared plan with real-time visibility.',
  },
];

const principles = [
  {
    icon: <FavoriteIcon />,
    title: 'Lived Experience',
    description: 'Created by people who have walked this path.',
  },
  {
    icon: < LocalHospitalIcon />,
    title: 'Clinical Excellence',
    description: 'Evidence-based templates and best-practice methods.',
  },
  {
    icon: <DesignServicesIcon />,
    title: 'Human-Centred Engineering',
    description: 'Simple, accessible design for cognitive needs.',
  },
  {
    icon: <EmojiPeopleIcon />,
    title: 'Empowerment',
    description: 'Technology that supports, never replaces, clinical judgement.',
  },
];

function AboutPage() {
  useSEO(PageSEO.about);
  useBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'About', url: '/about' }
  ]);

  const theme = useTheme();

  return (
    <>
      {/* Hero Section */}
      <Hero
        variant="gradient"
        showScrollIndicator={true}
        centered={false}
        maxWidth={false}
      >
        {/* Brand-atmosphere layer: cool mesh wash + Care Circle constellation
            (signature motif) fading from the top-right shoulder. Anchors to the
            Hero's relative outer Box so it spans the full-bleed band. Subtle per
            the visual-language brief — blue ≤10%, cyan ≤6%. */}
        <BrandBackdrop
          mesh="light"
          constellation
          constellationCorner="top-right"
          constellationOpacity={0.6}
        />
        <Grid container spacing={6} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 2,
                mb: 2,
                display: 'block',
              }}
            >
              About Us
            </Typography>
            <Typography variant="h1" sx={{ mb: 3 }}>
              Built from{' '}
              <Box component="span" sx={{ color: 'primary.main' }}>
                lived experience
              </Box>
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 4, fontSize: '1.25rem' }}
            >
              Making evidence-based neurorehabilitation easier to deliver every day—between sessions, across teams, and around the person.
            </Typography>
            {/* CTA hierarchy matched to a credibility-evaluating reader
                (investor / procurement / partner), not a self-serve clinician:
                primary is a low-friction "talk to the team", secondary is an
                even-lower-commitment product overview (ABOUT-5). */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact-us"
                sx={{ px: 4 }}
              >
                Talk to the team
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/solutions/professionals"
                sx={{ px: 4 }}
              >
                See how it works
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            {/* Frame the existing product visual forward: orb-halo brand motif
                glows behind the showcase, giving navy-tinted depth without
                inventing a fake card (a real visual exists). */}
            <Box sx={{ position: 'relative' }}>
              <BrandBackdrop
                motif="orb-halo"
                motifCorner="top-right"
                motifColor="37,99,235"
                motifOpacity={0.16}
                motifSize={460}
              />
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <ScreenshotShowcase />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Hero>

      {/* Origin Story */}
      <Section variant="gradient">
        <Container maxWidth="lg">
          <Box sx={{ mx: 'auto' }}>
            <SectionHeader
              title="Our Story"
              subtitle="The company was founded after Luke Parry sustained a traumatic brain injury in 2012 while studying engineering at Oxford with his twin brother Ellis."
              centered
            />

            <Grid container spacing={3} sx={{ mb: 8 }}>
              <Grid item xs={12} md={4}>
                <StoryCard
                  tag="Caregiver Perspective"
                  tagColor="audience.individual.main"
                  title="The gaps in care"
                  description="Seeing firsthand the lack of support between clinical sessions, motivated Ellis to build a bridge between the clinic and home."
                  ctaLabel="Read Ellis's Story"
                  ctaHref="/resources/blog/our-story"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StoryCard
                  tag="Patient Perspective"
                  tagColor="audience.individual.main"
                  title="Living the challenge"
                  description="Luke's brain injury exposed the real-world struggles of recovery: remembering, keeping momentum, trying to find himself again."
                  ctaLabel="Read Luke's Journey"
                  ctaHref="/resources/case-studies/lukes-journey"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StoryCard
                  tag="Expertise"
                  tagColor="audience.other.main"
                  title="Experience meets expertise"
                  description="Combine lived-expereince with gold-standard clinical expertise and engineering excellence to deliver tools that fit real life and support the moments that matter."
                  ctaLabel="See Our Approach"
                  ctaHref="/solutions/clients/#how-neumind-helps"
                />
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Section>

      {/* Team Section */}
      <Section centered>
        <SectionHeader
          title="Our Team"
          subtitle="The people behind neumind."
          centered
        />
        <Grid container spacing={3}>
          {team.map((member) => (
            <Grid item xs={12} sm={6} md={4} key={member.name}>
              <PersonCard {...member} variant="team" />
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* Advisors */}
      <Section variant="gradient" py={8}>
        <SectionHeader
          title="Advisory Board"
          titleVariant="h3"
          centered
        />
        <Grid container spacing={3} justifyContent="center" pb={2}>
          {advisors.map((advisor) => (
            <Grid item xs={12} sm={6} md={4} key={advisor.name}>
              <PersonCard {...advisor} variant="advisor" />
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* Features Section */}
      <Section centered variant="dark">
        <SectionHeader
          title="What neumind Does"
          subtitle="A complete digital neurorehabilitation toolkit for NHS teams and beyond."
          centered
          variant="dark"
        />
        <Grid container spacing={3}>
          {features.map((feature) => (
            <Grid item xs={12} sm={6} md={3} key={feature.title}>
              <InfoCard
                title={feature.title}
                description={feature.description}
                variant="glass"
                centered
                icon={<FeatureIcon icon={feature.icon} color={theme.palette.primary.light} />}
              />
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* Principles Section */}
      <Section centered>
        <SectionHeader
          title="Our Principles"
          centered
        />
        <Grid container spacing={3}>
          {principles.map((principle) => (
            <Grid item xs={12} sm={6} md={3} key={principle.title}>
              <InfoCard
                title={principle.title}
                description={principle.description}
                variant="default"
                centered
                icon={
                  <FeatureIcon
                    icon={principle.icon}
                    // Passing gradient logic is complex for simple color prop. 
                    // For now using primary.main to match default FeatureIcon
                    // or we can allow sx override in FeatureIcon.
                    // The original design had a gradient background for the icon.
                    // Let's stick to simple color for consistency with the design system first,
                    // or use sx to override if critical.
                    sx={{
                      background: 'linear-gradient(135deg, rgba(77, 101, 255, 0.1) 0%, rgba(14, 165, 223, 0.1) 100%)',
                      color: 'primary.main'
                    }}
                  />
                }
              />
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* Partners & supporters — honest tagline: this set mixes award bodies,
          research collaborators, investors, and customers, so it must not be
          framed as "trusted by"/customers (ABOUT-4). */}
      <LogoMarqueeSection tagline="Backed and supported by" speed={40} />

      {/* CTA Section */}
      <Box sx={{ py: 12, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <CallToActionCard
            location="about_bottom_cta"
            title="Join the Journey"
            description="Whether you're a clinician looking to extend your impact, or someone seeking better support in your recovery journey, we'd love to hear from you."
            primaryCTA={{ label: "Talk to the team", href: "/contact-us" }}
            secondaryCTA={{ label: "Get the app", href: ExternalUrls.dashboardSignup, external: true }}
          />
        </Container>
      </Box>
    </>
  );
}
